import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';

import CollabInviteMutualList from './CollabInviteMutualList';


import Stack from '@mui/joy/Stack';
import Grid from '@mui/joy/Grid';

import Typography from '@mui/joy/Typography';

import Divider from '@mui/joy/Divider';

import Chip from '@mui/joy/Chip';
import { CollabInviteProps } from 'stores/collab_invite_store';
import { CollabProps } from 'stores/collab_store';

import IdentityAvatar from './IdentityAvatar';
import IdentityBio from './IdentityBio';
import LoadingSection from 'components/LoadingSection';
import CombinedRequestAndCollabList from 'components/CombinedRequestAndCollabList';

import useCollabs from 'hooks/useCollabs';
import useCollabInvite from '../hooks/useCollabInvite';
import { getIcon } from 'utils/misc';
import useFHRequest from 'hooks/useFHRequest';
import useWorkKey from 'hooks/useWorkKey';
import RequestListItem from './RequestListItem';
import { FHRequestType } from 'stores/fhrequest_store';
import useUserIdentity from 'hooks/useUserIdentity';

import InviteActions from './InviteActions';


const InviteCollabPreview = observer(({ collab_uuid }: { collab_uuid: string }) => {
    const { getCollab, fetchCollab, collabs } = useCollabs();
    const [collab, setCollab] = useState<CollabProps|null>(getCollab(collab_uuid));
    const { currentIdentity } = useUserIdentity()

    const GoalIcon = getIcon("CheckCircleOutlineOutlined");
    const IdentityIcon = getIcon("PersonOutline");

    useEffect(() => {
        if(currentIdentity?.uuid) {
            fetchCollab(currentIdentity.uuid, collab_uuid);
        }
    }, [collab_uuid, currentIdentity.uuid]);

    useEffect(() => {
        const c = getCollab(collab_uuid);
        setCollab(c);
    }, [collabs.length, collab_uuid]);

    if (!collab) {
        return null;
    }
    return (
        <Stack spacing={1}>
            <Grid container>
                {/* collab/invite Info */}
                <Grid xs={12} sm={6}>

                    <Stack p={1} m={1} spacing={1}>
                        <Typography
                            startDecorator={<GoalIcon />}
                            level="title-sm">
                            Collab Details
                        </Typography>

                        <Divider />
                        <Stack>

                            <Typography
                                level="title-md">
                                {collab.title}
                            </Typography>


                            <Typography
                                level="body-md">
                                {collab.description}
                            </Typography>
                        </Stack>
                    </Stack>

                </Grid>
                {/* client Info */}
                <Grid xs={12} sm={6}>
                    <Stack p={1} m={1} spacing={1}>
                        <Typography
                            startDecorator={<IdentityIcon />}
                            level="title-sm">
                            Client Details
                        </Typography>

                        <Divider />
                        <IdentityAvatar size="inline"
                            interactive={true}
                            identityKey={collab?.identity} />

                        <IdentityBio identityKey={collab?.identity} />
                    </Stack>
                </Grid>
            </Grid>
        </Stack>
    );
});

function InviteMessage({ invite }: { invite: CollabInviteProps }) {
    return (
        <Stack>
            <Stack direction="row" spacing={1}>
                <Typography level="title-lg">
                    You were invited by
                </Typography>
                <Typography level="title-lg">
                    <IdentityAvatar size="inline" identityKey={invite.invited_by} />
                </Typography>
            </Stack>
            <Typography level="body-md">
                {invite.message}
            </Typography>
        </Stack>
    );
}

export default function InvitesRoute() {
    /** we show the single invite on this screen */
    const { inviteKey = "" } = useParams<{ inviteKey: string }>();
    const { currentIdentity } = useUserIdentity();
    const workKey = useWorkKey();
    const { getCollabInvite, fetchCollabInvite } = useCollabInvite();
    const invite = getCollabInvite(inviteKey);

    useEffect(() => {
        if (inviteKey && workKey && !invite) {
            fetchCollabInvite(workKey, inviteKey);
        }
    }, [inviteKey, currentIdentity?.uuid]);

    if (!invite) {
        return null;
    }
    return (
        <Stack spacing={3} padding={2}>
        { invite.message && invite.invited_by &&
            <InviteMessage invite={invite} />
        }
            <InviteCollabPreview collab_uuid={invite.associated_uuid} />
            <InviteActions invite={invite} />
        </Stack>
    );
}

export const RequestsAvailableList = observer(({ paged = false }: { paged?: boolean }) => {
    const { fetchListAvailable, getListAvailable } = useFHRequest();
    const workKey = useWorkKey();
    const [page, setPage] = useState(1);
    const DownIcon = getIcon('ArrowDropDown');
    const PAGE_SIZE = 3;
    const [requests, setRequests] = useState(getListAvailable());
    const showing = paged ? requests.slice(0, page * PAGE_SIZE) : requests;
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!workKey) return;
        if (loading) return;
        setLoading(true);
        fetchListAvailable(workKey).then(() => {
            setLoading(false);
            setRequests(getListAvailable());
        });
    }, [fetchListAvailable, workKey]);

    useEffect(() => {
        if (getListAvailable) {
            setRequests(getListAvailable());
        }
    }, [getListAvailable().length]);


    if (loading && !requests.length) {
        return <LoadingSection />;
    }

    return (
        <Stack>
            {showing.map((request: FHRequestType) => {
                return (
                    <RequestListItem
                        key={request.key}
                        request={request}
                    />
                );
            })}

            {paged &&
                <Stack
                    alignItems="center"
                    direction="row"
                    spacing={1}
                    justifyContent="center" sx={{ py: 2 }}>

                    {page > 1 && (
                        <Chip
                            variant="outlined"
                            color="neutral"
                            onClick={() => setPage(page - 1)}
                            endDecorator={<DownIcon sx={{ transform: 'rotate(180deg)' }} />}
                            slotProps={{ root: { component: 'span' } }}
                        >Show Fewer</Chip>
                    )}
                    {requests.length > page * PAGE_SIZE && (
                        <Chip
                            variant="outlined"
                            color="neutral"
                            onClick={() => setPage(page + 1)}
                            endDecorator={<DownIcon />}
                            slotProps={{ root: { component: 'span' } }}
                        >Show More</Chip>
                    )}

                </Stack>
            }

        </Stack>

    );
});


export function AvailableCollabInvitesRoute() {
    useEffect(() => {
        document.title = 'Available Collabs and Requests';
    }, []);
    return (
        <Stack spacing={2}>
            <CombinedRequestAndCollabList
                paged={false} />
        </Stack>
    );
}


export function MutualCollabInvitesRoute() {
    return (
        <Stack sx={{ height: '100dvh', overflow: "scroll" }}>
            <Typography p={2} level="title-lg">
                Collabs you've been invited to
            </Typography>

            <CollabInviteMutualList />
        </Stack>

    );
}

// export function InvitePage() {

// }
